import { useState, useEffect, useMemo } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Card, Btn, Spinner, ErrorMsg, Badge, Icon, Modal, ModalFooter, useToast } from '../components/ui';
import { InlineSearchPicker } from '../components/tasks/TaskComponents';

// Admin tool: move a deal detail (a single loan / property / investment) to a
// different deal, or merge an entire deal's contents into another deal. Both
// re-point the parent DealID in the API and pull every attached record along.

const MODES = [
  { key: 'detail', label: 'Move a deal detail' },
  { key: 'deal',   label: 'Merge an entire deal' },
];

const dealItems = (deals, excludeId) =>
  (deals || [])
    .filter(d => d.dealID !== excludeId)
    .map(d => ({
      value: d.dealID,
      label: d.name,
      dealID: d.dealID,
      subtitle: `${d.status}${d.detailCount ? ` · ${d.detailCount} detail${d.detailCount === 1 ? '' : 's'}` : ''}`,
    }));

export const MoveDeals = () => {
  const { toast } = useToast();
  const { data: deals, loading, error, reload } = useApi(() => api.get('deals'), []);

  const [mode, setMode]           = useState('detail');
  const [sourceDealId, setSource] = useState('');
  const [targetDealId, setTarget] = useState('');
  const [detailId, setDetailId]   = useState('');
  const [details, setDetails]     = useState(null);   // details of the source deal (detail mode)
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moving, setMoving]       = useState(false);

  // Load the source deal's details whenever it changes (detail mode only).
  useEffect(() => {
    if (mode !== 'detail' || !sourceDealId) { setDetails(null); setDetailId(''); return; }
    let cancelled = false;
    setDetailsLoading(true); setDetailId('');
    (async () => {
      try {
        const rows = await api.get(`deals/${sourceDealId}/details`);
        if (!cancelled) setDetails(rows || []);
      } catch (e) {
        if (!cancelled) { setDetails([]); toast.error(e.message); }
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceDealId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const sourceItems = useMemo(() => dealItems(deals), [deals]);
  const targetItems = useMemo(() => dealItems(deals, sourceDealId), [deals, sourceDealId]);
  const detailItems = useMemo(() => (details || []).map(d => ({
    value: d.dealDetailID,
    label: d.description || d.detailType || 'Detail',
    subtitle: d.detailType,
  })), [details]);

  const dealName   = (id) => (deals || []).find(d => d.dealID === id)?.name || '';
  const detailName = (id) => detailItems.find(d => d.value === id)?.label || '';

  const resetSelection = () => { setSource(''); setTarget(''); setDetailId(''); setDetails(null); };

  const canMove = mode === 'detail'
    ? Boolean(sourceDealId && detailId && targetDealId)
    : Boolean(sourceDealId && targetDealId);

  const doMove = async () => {
    setMoving(true);
    try {
      if (mode === 'detail') {
        await api.post(`deals/${sourceDealId}/details/${detailId}/move`, { targetDealId });
        toast.success(`Moved “${detailName(detailId)}” to “${dealName(targetDealId)}”.`);
      } else {
        const r = await api.post(`deals/${sourceDealId}/move`, { targetDealId });
        toast.success(`Moved ${r.detailsMoved} detail(s) into “${dealName(targetDealId)}”. The source deal is now empty.`);
      }
      setConfirmOpen(false);
      resetSelection();
      reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMoving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const sourceDetailCount = (deals || []).find(d => d.dealID === sourceDealId)?.detailCount ?? 0;

  return (
    <div>
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Move Deals & Details</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Re-parent a deal detail to a different deal, or merge one deal's entire contents into another.
          Everything attached — notes, tasks, documents, leads, contacts, loan/rent schedules, parcels — moves along.
          Documents are relocated in the destination deal's SharePoint folder too.
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {MODES.map(m => (
            <Btn key={m.key} size="sm" variant={mode === m.key ? 'primary' : 'secondary'}
              onClick={() => { setMode(m.key); resetSelection(); }}>
              {m.label}
            </Btn>
          ))}
        </div>

        <InlineSearchPicker
          label={mode === 'detail' ? 'Source deal (currently holds the detail)' : 'Source deal (contents move OUT)'}
          items={sourceItems}
          value={sourceDealId}
          onChange={(v) => { setSource(v); setTarget(''); }}
          placeholder="Search deals…"
        />

        {mode === 'detail' && sourceDealId && (
          detailsLoading
            ? <div style={{ margin: '4px 0 16px', fontSize: 13, color: B.textMd }}>Loading details…</div>
            : detailItems.length === 0
              ? <div style={{ margin: '4px 0 16px', fontSize: 13, color: B.amber }}>This deal has no deal details to move.</div>
              : <InlineSearchPicker
                  label="Deal detail to move"
                  items={detailItems}
                  value={detailId}
                  onChange={setDetailId}
                  placeholder="Select a deal detail…"
                />
        )}

        <InlineSearchPicker
          label={mode === 'detail' ? 'Destination deal' : 'Destination deal (contents move IN)'}
          items={targetItems}
          value={targetDealId}
          onChange={setTarget}
          placeholder="Search deals…"
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <Btn variant="primary" disabled={!canMove || moving} onClick={() => setConfirmOpen(true)}>
            <Icon name="swap" size={14} /> {mode === 'detail' ? 'Move Detail' : 'Merge Deal'}
          </Btn>
          {mode === 'deal' && sourceDealId && (
            <Badge color={B.textLt}>{sourceDetailCount} detail{sourceDetailCount === 1 ? '' : 's'} will move</Badge>
          )}
        </div>
      </Card>

      {confirmOpen && (
        <Modal title={mode === 'detail' ? 'Move deal detail?' : 'Merge deal?'} onClose={() => !moving && setConfirmOpen(false)}>
          <div style={{ fontSize: 14, color: B.text, lineHeight: 1.6 }}>
            {mode === 'detail' ? (
              <>
                Move <strong>{detailName(detailId)}</strong> from <strong>{dealName(sourceDealId)}</strong> to{' '}
                <strong>{dealName(targetDealId)}</strong>?
                <div style={{ fontSize: 12.5, color: B.textMd, marginTop: 10 }}>
                  All notes, tasks, documents, leads and contacts attached to this detail move with it.
                </div>
              </>
            ) : (
              <>
                Move <strong>all {sourceDetailCount} detail(s)</strong> and every note, task, document, lead and
                contact from <strong>{dealName(sourceDealId)}</strong> into <strong>{dealName(targetDealId)}</strong>?
                <div style={{ fontSize: 12.5, color: B.textMd, marginTop: 10 }}>
                  The source deal will be left empty (not deleted) so you can review and remove it afterwards.
                </div>
              </>
            )}
          </div>
          <ModalFooter>
            <Btn variant="secondary" disabled={moving} onClick={() => setConfirmOpen(false)}>Cancel</Btn>
            <Btn variant="primary" loading={moving} onClick={doMove}>
              {mode === 'detail' ? 'Move Detail' : 'Merge Deal'}
            </Btn>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};
